import React, { useState, useMemo, useEffect } from 'react';
import './Home.css';
import axios from 'axios';
import { 
    ResponsiveContainer, 
    AreaChart, 
    Area, 
    CartesianGrid, 
    XAxis, 
    YAxis, 
    Tooltip, 
    Legend 
} from 'recharts';
import { useRevit } from '../../contexts/RevitContext';
import { handleLoginWithYouTube, logoutFromYt } from '../../utils/apiCalls';
import { useAuth } from '@clerk/clerk-react';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import Icon from '../Icon';
import { TrafficSourceLogo, ResourceSourceLogo, classifyResourceType, classifyTrafficSource } from '../SourceLogo/SourceLogo';
import { applyDashboardTimeframe, endOfLocalDay, localDateString, startOfLocalDay } from '../../utils/dateRange';
import { LayoutDashboard, Eye, MousePointerClick, Link2, PhoneCall, Mail, Filter, ShoppingBag, DollarSign, Sparkles } from 'lucide-react';

const SERVER_URL = process.env.REACT_APP_SERVER_URL;

const hasClickResource = (click) => !!(click?.type || click?.resource_name || click?.youtube_title);

const CONTRIBUTOR_CAP = 5;

const emptyContributorMaps = (funnelStepsList = []) => ({
    clicks: {},
    attributed_clicks: {},
    sales: {},
    signups: {},
    calls: {},
    ...funnelStepsList.reduce((acc, stepName) => {
        acc[stepName] = {};
        return acc;
    }, {}),
});

const mergeContributorBucket = (target = {}, source = {}) => {
    const out = { ...target };
    Object.entries(source).forEach(([key, item]) => {
        if (!out[key]) {
            out[key] = { ...item };
            return;
        }
        out[key] = {
            ...out[key],
            count: (out[key].count || 0) + (item.count || 0),
            revenue: (out[key].revenue || 0) + (item.revenue || 0),
        };
    });
    return out;
};

const mergeContributorMaps = (target = {}, source = {}, funnelStepsList = []) => {
    const metrics = ['clicks', 'attributed_clicks', 'sales', 'signups', 'calls', ...funnelStepsList];
    const out = {};
    metrics.forEach((metric) => {
        out[metric] = mergeContributorBucket(target[metric], source[metric]);
    });
    return out;
};

const listifyContributors = (map, sortKey) => Object.values(map || {})
    .sort((a, b) => (b[sortKey] || 0) - (a[sortKey] || 0) || (b.count || 0) - (a.count || 0))
    .slice(0, CONTRIBUTOR_CAP);

const contributorListsFromMaps = (maps = {}, funnelStepsList = []) => ({
    clicks: listifyContributors(maps.clicks, 'count'),
    attributed_clicks: listifyContributors(maps.attributed_clicks, 'count'),
    sales: listifyContributors(maps.sales, 'count'),
    revenue: listifyContributors(maps.sales, 'revenue'),
    signups: listifyContributors(maps.signups, 'count'),
    calls: listifyContributors(maps.calls, 'count'),
    ...funnelStepsList.reduce((acc, stepName) => {
        acc[stepName] = listifyContributors(maps[stepName], 'count');
        return acc;
    }, {}),
});

const bumpSourceContributor = (maps, event) => {
    const classified = classifyTrafficSource({
        type: event?.type,
        referrer: event?.referrer,
        qrSource: event?.qr_source || event?.qrSource,
    });
    if (!maps.clicks[classified.id]) {
        maps.clicks[classified.id] = {
            kind: 'source',
            id: classified.id,
            name: classified.name,
            count: 0,
        };
    }
    maps.clicks[classified.id].count++;
};

const bumpResourceContributor = (maps, metric, event, revenue = 0) => {
    if (!maps[metric]) maps[metric] = {};
    const title = event?.youtube_title || event?.resource_name || 'Unattributed';
    const type = event?.type || '';
    const key = (event?.youtube_title || event?.resource_name || event?.type)
        ? `r:${type}:${title}`
        : 'unattributed';
    if (!maps[metric][key]) {
        maps[metric][key] = {
            kind: 'resource',
            title,
            type,
            count: 0,
            revenue: 0,
        };
    }
    maps[metric][key].count++;
    maps[metric][key].revenue += revenue;
};

const Home = ({ isProAccount, isFounder }) => {
    const [customStartDate, setCustomStartDate] = useState(() => applyDashboardTimeframe('30days').startDate);
    const [customEndDate, setCustomEndDate] = useState(() => applyDashboardTimeframe('30days').endDate);
    const [timeframeOption, setTimeframeOption] = useState('30days');
    const [haveViews, setHaveViews] = useState(true);
    const [selectedOffer, setSelectedOffer] = useState(null); // Add this state for offer selection
    const [dailyData, setDailyData] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeMetric, setActiveMetric] = useState('clicks');
    const [loggingOutYt, setLoggingOutYt] = useState(false);
    const [periodComparison, setPeriodComparison] = useState({
        views: { current: 0, previous: 0 },
        engagedViews: { current: 0, previous: 0 },
        clicks: { current: 0, previous: 0 },
        attributed_clicks: { current: 0, previous: 0 },
        sales: { current: 0, previous: 0 },
        signups: { current: 0, previous: 0 },
        revenue: { current: 0, previous: 0 },
        calls: { current: 0, previous: 0 }
    });
    const [hasComparisonData, setHasComparisonData] = useState(false);
    const [selectedSource, setSelectedSource] = useState(null); // Add state for source selection
    const [currency, setCurrency] = useState('USD');
    const [hasSignupOffers, setHasSignupOffers] = useState(false); // Add state for signup offers
    const [hasCallOffers, setHasCallOffers] = useState(false); // Add state for call offers
    const [offers, setOffers] = useState([]); // Add state to track offers
    const [isStepSelectorOpen, setIsStepSelectorOpen] = useState(false);
    const [funnelStepsList, setFunnelStepsList] = useState([]);
    const [visibleSteps, setVisibleSteps] = useState(() => {
        const saved = localStorage.getItem('visibleFunnelSteps');
        return saved ? JSON.parse(saved) : {};
    });
    const [viewBy, setViewBy] = useState('day');
    const [topResources, setTopResources] = useState(null);
    const [topResourcesTab, setTopResourcesTab] = useState('sales');
    const [offerLeaderboardTab, setOfferLeaderboardTab] = useState('revenue');
    const [sourceLeaderboardTab, setSourceLeaderboardTab] = useState('revenue');
    const [aiInsights, setAiInsights] = useState(null);
    const [aiInsightsLoading, setAiInsightsLoading] = useState(false);
    const [offerSort, setOfferSort] = useState({ key: null, dir: 'desc' });
    const [sourceSort, setSourceSort] = useState({ key: null, dir: 'desc' });
    const [contentSort, setContentSort] = useState({ key: null, dir: 'desc' });
    const [showEngagedViews, setShowEngagedViews] = useState(() => {
        const saved = localStorage.getItem('showEngagedViews');
        return saved == null ? true : saved === 'true';
    });

    // Update localStorage whenever visibleSteps changes
    useEffect(() => {
        localStorage.setItem('visibleFunnelSteps', JSON.stringify(visibleSteps));
    }, [visibleSteps]);

    useEffect(() => {
        localStorage.setItem('showEngagedViews', String(showEngagedViews));
        if (!showEngagedViews && activeMetric === 'engagedViews') {
            setActiveMetric('summary');
        }
    }, [showEngagedViews, activeMetric]);


    // Update visibleSteps when new funnel steps appear
    useEffect(() => {
        if (funnelStepsList) {
            setVisibleSteps(prev => {
                const updated = { ...prev };
                let hasChanges = false;
                
                funnelStepsList.forEach(stepName => {
                    if (!(stepName in updated)) {
                        updated[stepName] = false; // Default to hidden for new steps
                        hasChanges = true;
                    }
                });
                
                return hasChanges ? updated : prev;
            });
        }
    }, [funnelStepsList]);

    const { username, youtubeName, setYoutubeName, youtubeThumbUrl } = useRevit();
    const { getToken } = useAuth();

    const handleLogout = async () => {
            const token = await getToken();
            setLoggingOutYt(true);
            try {
                const result = await logoutFromYt(username, token);
                if (result) {
                    setYoutubeName(null);
                }
            } catch (error) {
                console.error('Error during logout:', error);
            } finally {
                setLoggingOutYt(false);
            }
        };

    // Reset column sort when tabs change
    useEffect(() => { setOfferSort({ key: null, dir: 'desc' }); }, [offerLeaderboardTab]);
    useEffect(() => { setSourceSort({ key: null, dir: 'desc' }); }, [sourceLeaderboardTab]);
    useEffect(() => { setContentSort({ key: null, dir: 'desc' }); }, [topResourcesTab]);

    const makeHandleSort = (setter) => (key) =>
        setter(prev => prev.key === key
            ? { key, dir: prev.dir === 'desc' ? 'asc' : 'desc' }
            : { key, dir: 'desc' });
    const handleOfferSort   = makeHandleSort(setOfferSort);
    const handleSourceSort  = makeHandleSort(setSourceSort);
    const handleContentSort = makeHandleSort(setContentSort);

    const SortTh = ({ label, sortKey, sort, onSort, className }) => {
        const isActive = sort.key === sortKey;
        return (
            <span
                className={`${className || ''} th-sortable${isActive ? ' th-sort-active' : ''}`}
                onClick={() => onSort(sortKey)}
            >
                {label}
                <span className="th-sort-icon">{isActive ? (sort.dir === 'desc' ? '↓' : '↑') : '↕'}</span>
            </span>
        );
    };

    const fetchAiInsights = async () => {
        if (!username) return;
        setAiInsightsLoading(true);
        setAiInsights(null);
        try {
            const token = await getToken();
            const response = await axios.get(
                `https://${SERVER_URL}/api/ai-insights/${username}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setAiInsights(response.data);
        } catch (err) {
            console.error('Error fetching AI insights:', err);
        } finally {
            setAiInsightsLoading(false);
        }
    };

    const processRawData = (rawData, funnelStepsList = []) => {
        // Helper function to get local date string without timezone conversion
        const getLocalDateString = (timestamp) => localDateString(timestamp);

        const dailyData = {};
        let viewsByDate = {};
        let engagedViewsByDate = {};

        // Process YouTube views data into a map for easy lookup
        if (rawData.views) {
            rawData.views.forEach(viewData => {
                viewsByDate[viewData.date] = viewData.views;
                engagedViewsByDate[viewData.date] = viewData.engagedViews;
            });
        } else {
            viewsByDate = null;
            engagedViewsByDate = null;
        }

        const sourceIdFromEvent = (event) => classifyTrafficSource({
            type: event?.type,
            referrer: event?.referrer,
            qrSource: event?.qr_source || event?.qrSource,
        }).id;

        const emptyTypeMetrics = (date, sourceId) => ({
            views: sourceId === 'youtube' ? (viewsByDate ? (viewsByDate[date] || 0) : null) : null,
            engagedViews: sourceId === 'youtube' ? (engagedViewsByDate ? (engagedViewsByDate[date] || 0) : null) : null,
            clicks: 0,
            attributed_clicks: 0,
            sales: 0,
            signups: 0,
            revenue: 0,
            calls: 0,
            ...funnelStepsList.reduce((acc, stepName) => { acc[stepName] = 0; return acc; }, {})
        });

        // Get unique dates from clicks, sales, signups, calls, views, and funnel steps
        const dates = new Set([
            ...rawData.clicks.map(click => getLocalDateString(click.timestamp)),
            ...rawData.sales.map(sale => getLocalDateString(sale.timestamp)),
            ...(rawData.signups ? rawData.signups.map(signup => getLocalDateString(signup.timestamp)) : []),
            ...(rawData.call_bookings ? rawData.call_bookings.map(call => getLocalDateString(call.timestamp)) : []),
            ...(rawData.views ? rawData.views.map(view => view.date) : []),
            ...(rawData.funnel_steps ? Object.values(rawData.funnel_steps).flat().map(step => getLocalDateString(step.timestamp)) : [])
        ]);

        // Initialize data structure for each date
        dates.forEach(date => {
            dailyData[date] = {
                date,
                metrics: {
                    overall: { 
                        views: viewsByDate ? (viewsByDate[date] || 0) : null,
                        engagedViews: engagedViewsByDate ? (engagedViewsByDate[date] || 0) : null, 
                        clicks: 0,
                        attributed_clicks: 0,
                        sales: 0,
                        signups: 0,
                        revenue: 0,
                        calls: 0,
                        ...funnelStepsList.reduce((acc, stepName) => {
                            acc[stepName] = 0;
                            return acc;
                        }, {}),
                        contributorMaps: emptyContributorMaps(funnelStepsList),
                    },
                    byType: {},
                    byOffer: {},
                }
            };
            
            dailyData[date].metrics.byType.youtube = emptyTypeMetrics(date, 'youtube');
        });

        const typeBucket = (date, event) => {
            const sourceId = sourceIdFromEvent(event);
            if (!dailyData[date].metrics.byType[sourceId]) {
                dailyData[date].metrics.byType[sourceId] = emptyTypeMetrics(date, sourceId);
            }
            return dailyData[date].metrics.byType[sourceId];
        };

        const offerBucket = (date, offer_name) => {
            if (!dailyData[date].metrics.byOffer[offer_name]) {
                dailyData[date].metrics.byOffer[offer_name] = {
                    views: viewsByDate ? (viewsByDate[date] || 0) : null,
                    engagedViews: engagedViewsByDate ? (engagedViewsByDate[date] || 0) : null,
                    clicks: 0,
                    attributed_clicks: 0,
                    sales: 0,
                    signups: 0,
                    revenue: 0,
                    calls: 0,
                    ...funnelStepsList.reduce((acc, stepName) => {
                        acc[stepName] = 0;
                        return acc;
                    }, {}),
                    contributorMaps: emptyContributorMaps(funnelStepsList),
                };
            }
            return dailyData[date].metrics.byOffer[offer_name];
        };

        // Process funnel steps
        if (rawData.funnel_steps) {
            Object.entries(rawData.funnel_steps).forEach(([stepName, stepData]) => {
                stepData.forEach(step => {
                    const date = getLocalDateString(step.timestamp);
                    const { offer_name } = step;
                    const offer = offerBucket(date, offer_name);
                    dailyData[date].metrics.overall[stepName]++;
                    typeBucket(date, step)[stepName]++;
                    offer[stepName]++;
                    bumpResourceContributor(dailyData[date].metrics.overall.contributorMaps, stepName, step);
                    bumpResourceContributor(offer.contributorMaps, stepName, step);
                });
            });
        }

        // Process clicks
        rawData.clicks.forEach(click => {
            const date = getLocalDateString(click.timestamp);
            const { offer_name } = click;
            const offer = offerBucket(date, offer_name);

            dailyData[date].metrics.overall.clicks++;
            typeBucket(date, click).clicks++;
            offer.clicks++;
            bumpSourceContributor(dailyData[date].metrics.overall.contributorMaps, click);
            bumpSourceContributor(offer.contributorMaps, click);

            if (hasClickResource(click)) {
                dailyData[date].metrics.overall.attributed_clicks++;
                typeBucket(date, click).attributed_clicks++;
                offer.attributed_clicks = (offer.attributed_clicks || 0) + 1;
                bumpResourceContributor(dailyData[date].metrics.overall.contributorMaps, 'attributed_clicks', click);
                bumpResourceContributor(offer.contributorMaps, 'attributed_clicks', click);
            }
        });

        // Process sales
        rawData.sales.forEach(sale => {
            const date = getLocalDateString(sale.timestamp);
            const { offer_name, value } = sale;
            const saleValue = Number(value) || 0;
            const offer = offerBucket(date, offer_name);

            dailyData[date].metrics.overall.sales++;
            dailyData[date].metrics.overall.revenue += saleValue;

            const saleTypeBucket = typeBucket(date, sale);
            saleTypeBucket.sales++;
            saleTypeBucket.revenue += saleValue;

            offer.sales++;
            offer.revenue += saleValue;
            bumpResourceContributor(dailyData[date].metrics.overall.contributorMaps, 'sales', sale, saleValue);
            bumpResourceContributor(offer.contributorMaps, 'sales', sale, saleValue);
        });

        // Process signups
        if (rawData.signups) {
            rawData.signups.forEach(signup => {
                const date = getLocalDateString(signup.timestamp);
                const { offer_name } = signup;
                const offer = offerBucket(date, offer_name);

                dailyData[date].metrics.overall.signups++;
                typeBucket(date, signup).signups++;
                offer.signups++;
                bumpResourceContributor(dailyData[date].metrics.overall.contributorMaps, 'signups', signup);
                bumpResourceContributor(offer.contributorMaps, 'signups', signup);
            });
        }

        // Process call bookings
        if (rawData.call_bookings) {
            rawData.call_bookings.forEach(call => {
                const date = getLocalDateString(call.timestamp);
                const { offer_name } = call;
                const offer = offerBucket(date, offer_name);

                dailyData[date].metrics.overall.calls++;
                typeBucket(date, call).calls++;
                offer.calls++;
                bumpResourceContributor(dailyData[date].metrics.overall.contributorMaps, 'calls', call);
                bumpResourceContributor(offer.contributorMaps, 'calls', call);
            });
        }

        return Object.values(dailyData);
    };

    const fetchDashboardData = async (startDateIncludingPreviousPeriod, endDate, realStartDate) => {
        const token = await getToken();
        try {
            setIsLoading(true);
            setError(null);
            
            const response = await axios.get(
                `https://${SERVER_URL}/api/get-data/${username}`,
                {
                    params: {
                        startDateIncludingPreviousPeriod: startDateIncludingPreviousPeriod.toISOString(),
                        endDate: endDate.toISOString(),
                        realStartDate: realStartDate.toISOString(),
                    },
                    headers: {
                        'Authorization': `Bearer ${token}`,
                    }
                }
            );

            response.data.views == null ? setHaveViews(false) : setHaveViews(true);
            
            if (response.data.currency) {
                setCurrency(response.data.currency);
            }
            
            if (response.data.offers) {
                const hasSignups = response.data.offers.some(offer => offer.offer_type === 'signup');
                const hasCalls = response.data.offers.some(offer => offer.call_booking_required === true);
                setHasSignupOffers(hasSignups);
                setHasCallOffers(hasCalls);
            }

            if (response.data.funnel_steps) {
                setFunnelStepsList(Object.keys(response.data.funnel_steps));
            }
            
            const processedData = processRawData(response.data, Object.keys(response.data.funnel_steps));
            setDailyData(processedData);

            if (response.data.topResources) {
                console.log(response.data.topResources)
                setTopResources(response.data.topResources);
            }
            
            // Check if we have enough data for comparison based on custom date range
            const periodDays = Math.ceil((customEndDate - customStartDate) / (1000 * 60 * 60 * 24));
            const hasEnoughData = startDateIncludingPreviousPeriod <= new Date(customStartDate.getTime() - (periodDays * 24 * 60 * 60 * 1000));
            setHasComparisonData(hasEnoughData);
        } catch (err) {
            console.error('Error fetching dashboard data:', err);
            setError('Failed to load dashboard data');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (username && customStartDate && customEndDate) {
            const startDate = startOfLocalDay(customStartDate);
            const endDate = endOfLocalDay(customEndDate);

            // Pin to local-day edges so the backend's inclusive
            // `timestamp BETWEEN ... AND ...` filter actually covers every
            // event on the picked calendar days. The raw picker values sit
            // at local 00:00:00; without `setHours` on the end the upper
            // bound serialises to UTC midnight of the day *before* the
            // last picked day, silently dropping ~24h of events for any
            // user east of UTC.

            // Calculate the exact time period in milliseconds (N*24h - 1ms,
            // which is fine for the day-string comparison the frontend uses).
            const periodTime = endDate - startDate;

            // Fetch 2x the period to get comparison data
            const startDateIncludingPreviousPeriod = new Date(startDate.getTime() - periodTime);

            const fetchEndDate = endDate;

            setIsLoading(true);
            setHasComparisonData(false);

            fetchDashboardData(startDateIncludingPreviousPeriod, fetchEndDate, startDate);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [customStartDate, customEndDate, username]);

    useEffect(() => {
        if (dailyData.length > 0 && customStartDate && customEndDate) {
            
            // Helper function to get local date string without timezone conversion
            const getLocalDateString = (date) => localDateString(date);

            // Sort data by date
            const sortedData = [...dailyData].sort((a, b) => new Date(a.date) - new Date(b.date));

            // Filter data to only include dates within the selected range and comparison range
            const startDateStr = getLocalDateString(customStartDate);
            const endDateStr = getLocalDateString(customEndDate);

            // Split data into current and previous periods
            // Current: data between customStartDate and customEndDate
            const currentPeriodData = sortedData.filter(day => day.date >= startDateStr && day.date <= endDateStr);
            // Previous: all data before customStartDate
            const previousPeriodData = sortedData.filter(day => day.date < startDateStr);

            // Calculate totals for current period
            const currentTotals = currentPeriodData.reduce((acc, day) => {
                const result = {
                    views: acc.views + (Number(day.metrics.overall.views) || 0),
                    engagedViews: acc.engagedViews + (Number(day.metrics.overall.engagedViews) || 0),
                    clicks: acc.clicks + (Number(day.metrics.overall.clicks) || 0),
                    attributed_clicks: acc.attributed_clicks + (Number(day.metrics.overall.attributed_clicks) || 0),
                    sales: acc.sales + (Number(day.metrics.overall.sales) || 0),
                    signups: acc.signups + (Number(day.metrics.overall.signups) || 0),
                    revenue: acc.revenue + (Number(day.metrics.overall.revenue) || 0),
                    calls: acc.calls + (Number(day.metrics.overall.calls) || 0),
                };
                
                // Add funnel step totals
                funnelStepsList.forEach(stepName => {
                    result[stepName] = (acc[stepName] || 0) + (Number(day.metrics.overall[stepName]) || 0);
                });
                
                return result;
            }, { views: 0, engagedViews: 0, clicks: 0, attributed_clicks: 0, sales: 0, signups: 0, revenue: 0, calls: 0, ...funnelStepsList.reduce((acc, stepName) => { acc[stepName] = 0; return acc; }, {}) });

            // Calculate totals for previous period
            const previousTotals = previousPeriodData.reduce((acc, day) => {
                const result = {
                    views: acc.views + (Number(day.metrics.overall.views) || 0),
                    engagedViews: acc.engagedViews + (Number(day.metrics.overall.engagedViews) || 0),
                    clicks: acc.clicks + (Number(day.metrics.overall.clicks) || 0),
                    attributed_clicks: acc.attributed_clicks + (Number(day.metrics.overall.attributed_clicks) || 0),
                    sales: acc.sales + (Number(day.metrics.overall.sales) || 0),
                    signups: acc.signups + (Number(day.metrics.overall.signups) || 0),
                    revenue: acc.revenue + (Number(day.metrics.overall.revenue) || 0),
                    calls: acc.calls + (Number(day.metrics.overall.calls) || 0)
                };
                
                // Add funnel step totals
                funnelStepsList.forEach(stepName => {
                    result[stepName] = (acc[stepName] || 0) + (Number(day.metrics.overall[stepName]) || 0);
                });
                
                return result;
            }, { views: 0, engagedViews: 0, clicks: 0, attributed_clicks: 0, sales: 0, signups: 0, revenue: 0, calls: 0, ...funnelStepsList.reduce((acc, stepName) => { acc[stepName] = 0; return acc; }, {}) });
            
            // Update period comparison state
            setPeriodComparison({
                views: { current: currentTotals.views, previous: previousTotals.views },
                engagedViews: { current: currentTotals.engagedViews, previous: previousTotals.engagedViews },
                clicks: { current: currentTotals.clicks, previous: previousTotals.clicks },
                attributed_clicks: { current: currentTotals.attributed_clicks || 0, previous: previousTotals.attributed_clicks || 0 },
                sales: { current: currentTotals.sales, previous: previousTotals.sales },
                signups: { current: currentTotals.signups, previous: previousTotals.signups },
                revenue: { current: currentTotals.revenue, previous: previousTotals.revenue },
                calls: { current: currentTotals.calls, previous: previousTotals.calls },
                ...funnelStepsList.reduce((acc, stepName) => {
                    acc[stepName] = {
                        current: currentTotals[stepName],
                        previous: previousTotals[stepName]
                    };
                    return acc;
                }, {})
            });
            
            setHasComparisonData(previousPeriodData.length > 0);
        }
    }, [dailyData, customStartDate, customEndDate, funnelStepsList]);

    // Process data based on selected custom date range
    const processedData = useMemo(() => {
        if (!dailyData.length || !customStartDate || !customEndDate) return [];

        // Helper function to get local date string without timezone conversion
        const getLocalDateString = (date) => localDateString(date);

        // Sort data by date to ensure correct order
        const sortedData = [...dailyData].sort((a, b) => new Date(a.date) - new Date(b.date));

        // Filter data to only include dates within the selected range
        const startDateStr = getLocalDateString(customStartDate);
        const endDateStr = getLocalDateString(customEndDate);

        const currentPeriodData = sortedData.filter(day => {
            return day.date >= startDateStr && day.date <= endDateStr;
        });

        // Filter data based on selected offer or source
        return currentPeriodData.map(day => {
            if (selectedOffer) {
                // When an offer is selected, use that offer's metrics
                const offerMetrics = day.metrics.byOffer[selectedOffer] || { clicks: 0, sales: 0, signups: 0, revenue: 0, calls: 0, contributorMaps: emptyContributorMaps(funnelStepsList), ...funnelStepsList.reduce((acc, stepName) => { acc[stepName] = 0; return acc; }, {}) };
                
                return {
                    date: day.date,
                    views: Number(day.metrics.overall.views) || 0, // Always use overall views for offers
                    engagedViews: Number(day.metrics.overall.engagedViews) || 0,
                    clicks: Number(offerMetrics.clicks) || 0,
                    attributed_clicks: Number(offerMetrics.attributed_clicks) || 0,
                    sales: Number(offerMetrics.sales) || 0,
                    signups: Number(offerMetrics.signups) || 0,
                    revenue: Number(offerMetrics.revenue) || 0,
                    calls: Number(offerMetrics.calls) || 0,
                    contributorMaps: offerMetrics.contributorMaps || emptyContributorMaps(funnelStepsList),
                    // Add funnel step data
                    ...funnelStepsList.reduce((acc, stepName) => {
                        acc[stepName] = Number(offerMetrics[stepName]) || 0;
                        return acc;
                    }, {})
                };
            } else {
                // If no filter is selected, use overall metrics
                return {
                    date: day.date,
                    views: Number(day.metrics.overall.views) || 0,
                    engagedViews: Number(day.metrics.overall.engagedViews) || 0,
                    clicks: Number(day.metrics.overall.clicks) || 0,
                    attributed_clicks: Number(day.metrics.overall.attributed_clicks) || 0,
                    sales: Number(day.metrics.overall.sales) || 0,
                    signups: Number(day.metrics.overall.signups) || 0,
                    revenue: Number(day.metrics.overall.revenue) || 0,
                    calls: Number(day.metrics.overall.calls) || 0,
                    contributorMaps: day.metrics.overall.contributorMaps || emptyContributorMaps(funnelStepsList),
                    // Add funnel step data
                    ...funnelStepsList.reduce((acc, stepName) => {
                        acc[stepName] = day.metrics.overall[stepName] || 0;
                        return acc;
                    }, {})
                };
            }
        });
    }, [customStartDate, customEndDate, dailyData, selectedOffer, selectedSource, funnelStepsList]);

    const currentMetricTotals = useMemo(() => {
        const totals = {
            views: 0,
            engagedViews: 0,
            clicks: 0,
            attributed_clicks: 0,
            sales: 0,
            signups: 0,
            calls: 0,
            revenue: 0,
            ...funnelStepsList.reduce((acc, stepName) => {
                acc[stepName] = 0;
                return acc;
            }, {}),
        };
        processedData.forEach((day) => {
            totals.views += Number(day.views) || 0;
            totals.engagedViews += Number(day.engagedViews) || 0;
            totals.clicks += Number(day.clicks) || 0;
            totals.attributed_clicks += Number(day.attributed_clicks) || 0;
            totals.sales += Number(day.sales) || 0;
            totals.signups += Number(day.signups) || 0;
            totals.calls += Number(day.calls) || 0;
            totals.revenue += Number(day.revenue) || 0;
            funnelStepsList.forEach((stepName) => {
                totals[stepName] += Number(day[stepName]) || 0;
            });
        });
        return totals;
    }, [processedData, funnelStepsList]);

    const hasMetricValue = (metric) => (Number(currentMetricTotals[metric]) || 0) > 0;

    useEffect(() => {
        if (activeMetric === 'summary') return;
        if (!hasMetricValue(activeMetric)) {
            setActiveMetric('summary');
        }
    }, [activeMetric, currentMetricTotals]);

    const getNormalizedData = useMemo(() => {
        if (!processedData.length) return [];

        // Group data by time period if needed
        let dataToProcess = processedData;
        if (viewBy === 'week' || viewBy === 'month') {
            const grouped = {};
            
            processedData.forEach(item => {
                const date = new Date(item.date);
                let key;
                
                if (viewBy === 'week') {
                    // Get start of week (Sunday)
                    const startOfWeek = new Date(date);
                    startOfWeek.setDate(date.getDate() - date.getDay());
                    key = startOfWeek.toISOString().split('T')[0];
                } else if (viewBy === 'month') {
                    // Get year-month
                    key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-01`;
                }
                
                if (!grouped[key]) {
                    grouped[key] = {
                        date: key,
                        views: 0,
                        engagedViews: 0,
                        clicks: 0,
                        attributed_clicks: 0,
                        sales: 0,
                        signups: 0,
                        calls: 0,
                        revenue: 0,
                        ...funnelStepsList.reduce((acc, stepName) => {
                            acc[stepName] = 0;
                            return acc;
                        }, {})
                    };
                }
                
                grouped[key].views += item.views || 0;
                grouped[key].engagedViews += item.engagedViews || 0;
                grouped[key].clicks += item.clicks || 0;
                grouped[key].attributed_clicks += item.attributed_clicks || 0;
                grouped[key].sales += item.sales || 0;
                grouped[key].signups += item.signups || 0;
                grouped[key].calls += item.calls || 0;
                grouped[key].revenue += item.revenue || 0;
                
                funnelStepsList.forEach(stepName => {
                    grouped[key][stepName] += item[stepName] || 0;
                });
            });
            
            dataToProcess = Object.values(grouped).sort((a, b) => new Date(a.date) - new Date(b.date));
        }

        // Find maximum values for each metric
        const maxValues = dataToProcess.reduce((max, day) => ({
            views: Math.max(max.views, day.views || 0),
            clicks: Math.max(max.clicks, day.clicks || 0),
            sales: Math.max(max.sales, day.sales || 0),
            signups: Math.max(max.signups, day.signups || 0),
            calls: Math.max(max.calls, day.calls || 0),
            revenue: Math.max(max.revenue, day.revenue || 0),
            ...funnelStepsList.reduce((acc, stepName) => {
                acc[stepName] = Math.max(acc[stepName] || 0, day[stepName] || 0);
                return acc;
            }, {})
        }), { views: 0, engagedViews: 0, clicks: 0, sales: 0, signups: 0, calls: 0, revenue: 0, ...funnelStepsList.reduce((acc, stepName) => { acc[stepName] = 0; return acc; }, {}) });

        // Normalize data (scale from 0 to 100)
        return dataToProcess.map(day => ({
            date: day.date,
            normalizedViews: maxValues.views ? (day.views / maxValues.views) * 100 : 0,
            normalizedClicks: maxValues.clicks ? (day.clicks / maxValues.clicks) * 100 : 0,
            normalizedSales: maxValues.sales ? (day.sales / maxValues.sales) * 100 : 0,
            normalizedSignups: maxValues.signups ? (day.signups / maxValues.signups) * 100 : 0,
            normalizedCalls: maxValues.calls ? (day.calls / maxValues.calls) * 100 : 0,
            normalizedRevenue: maxValues.revenue ? (day.revenue / maxValues.revenue) * 100 : 0,
            // Keep original values for tooltips
            views: day.views,
            clicks: day.clicks,
            sales: day.sales,
            signups: day.signups,
            calls: day.calls,
            revenue: day.revenue,
            ...funnelStepsList.reduce((acc, stepName) => {
                acc[stepName] = day[stepName];
                return acc;
            }, {})
        }));
    }, [processedData, funnelStepsList, viewBy]);

    // Add grouped processed data
    const groupedProcessedData = useMemo(() => {
        const withContributorLists = (item) => {
            const { contributorMaps, ...rest } = item;
            return {
                ...rest,
                contributors: contributorListsFromMaps(contributorMaps, funnelStepsList),
            };
        };

        if (!processedData.length) return [];
        if (viewBy === 'day') return processedData.map(withContributorLists);

        const grouped = {};
        
        processedData.forEach(item => {
            const date = new Date(item.date);
            let key;
            
            if (viewBy === 'week') {
                const startOfWeek = new Date(date);
                startOfWeek.setDate(date.getDate() - date.getDay());
                key = startOfWeek.toISOString().split('T')[0];
            } else if (viewBy === 'month') {
                key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-01`;
            }
            
            if (!grouped[key]) {
                grouped[key] = {
                    date: key,
                    views: 0,
                    engagedViews: 0,
                    clicks: 0,
                    attributed_clicks: 0,
                    sales: 0,
                    signups: 0,
                    calls: 0,
                    revenue: 0,
                    contributorMaps: emptyContributorMaps(funnelStepsList),
                    ...funnelStepsList.reduce((acc, stepName) => {
                        acc[stepName] = 0;
                        return acc;
                    }, {})
                };
            }
            
            grouped[key].views += item.views || 0;
            grouped[key].engagedViews += item.engagedViews || 0;
            grouped[key].clicks += item.clicks || 0;
            grouped[key].attributed_clicks += item.attributed_clicks || 0;
            grouped[key].sales += item.sales || 0;
            grouped[key].signups += item.signups || 0;
            grouped[key].calls += item.calls || 0;
            grouped[key].revenue += item.revenue || 0;
            grouped[key].contributorMaps = mergeContributorMaps(
                grouped[key].contributorMaps,
                item.contributorMaps,
                funnelStepsList
            );
            
            funnelStepsList.forEach(stepName => {
                grouped[key][stepName] += item[stepName] || 0;
            });
        });
        
        return Object.values(grouped)
            .sort((a, b) => new Date(a.date) - new Date(b.date))
            .map(withContributorLists);
    }, [processedData, viewBy, funnelStepsList]);

    const offerMetrics = useMemo(() => {
        if (!dailyData || !dailyData.length || !customStartDate || !customEndDate) return [];

        // Filter data to only include dates within the selected range
        const startDateStr = localDateString(customStartDate);
        const endDateStr = localDateString(customEndDate);

        const sortedData = [...dailyData]
            .sort((a, b) => new Date(a.date) - new Date(b.date))
            .filter(day => day.date >= startDateStr && day.date <= endDateStr);

        // Aggregate clicks, sales, signups, and revenue by offer
        const offerStats = {};

        // Process each day's data (only current period)
        sortedData.forEach(day => {
            // Process metrics by offer
            Object.entries(day.metrics.byOffer || {}).forEach(([offerName, metrics]) => {
                if (!offerStats[offerName]) {
                    offerStats[offerName] = { 
                        clicks: 0, 
                        sales: 0, 
                        signups: 0,
                        calls: 0,
                        revenue: 0,
                        ...funnelStepsList.reduce((acc, stepName) => {
                            acc[stepName] = 0;
                            return acc;
                        }, {})
                    };
                }
                
                // Add clicks, sales, signups, and revenue
                offerStats[offerName].clicks += Number(metrics.clicks || 0);
                offerStats[offerName].sales += Number(metrics.sales || 0);
                offerStats[offerName].signups += Number(metrics.signups || 0);
                offerStats[offerName].calls += Number(metrics.calls || 0);
                offerStats[offerName].revenue += Number(metrics.revenue || 0);
                // Add funnel step data
                funnelStepsList.forEach(stepName => {
                    offerStats[offerName][stepName] += Number(metrics[stepName] || 0);
                });
                
                // Debug logging to check values
            });
        });


        // Convert to array and sort by clicks
        return Object.entries(offerStats)
            .map(([title, metrics]) => ({ 
                title, 
                metrics: {
                    clicks: metrics.clicks,
                    sales: metrics.sales,
                    signups: metrics.signups,
                    calls: metrics.calls,
                    revenue: metrics.revenue,
                    ...funnelStepsList.reduce((acc, stepName) => {
                        acc[stepName] = metrics[stepName];
                        return acc;
                    }, {})
                }
            }))
            .sort((a, b) => b.metrics.clicks - a.metrics.clicks);
    }, [dailyData, customStartDate, customEndDate]);


    const sourceMetrics = useMemo(() => {
        if (!dailyData || !dailyData.length || !customStartDate || !customEndDate) return [];

        // Filter data to only include dates within the selected range
        const startDateStr = localDateString(customStartDate);
        const endDateStr = localDateString(customEndDate);

        const sortedData = [...dailyData]
            .sort((a, b) => new Date(a.date) - new Date(b.date))
            .filter(day => day.date >= startDateStr && day.date <= endDateStr);

        const emptyMetrics = () => ({
            clicks: 0,
            sales: 0,
            signups: 0,
            calls: 0,
            revenue: 0,
            views: null,
        });

        const sourceStats = {};

        sortedData.forEach(day => {
            Object.entries(day.metrics.byType).forEach(([typeName, metrics]) => {
                const classified = classifyResourceType(typeName);
                if (!sourceStats[classified.id]) {
                    sourceStats[classified.id] = {
                        ...emptyMetrics(),
                        name: classified.name,
                        views: classified.id === 'youtube' ? 0 : null,
                    };
                }

                const bucket = sourceStats[classified.id];
                bucket.clicks += (metrics.clicks || 0);
                bucket.sales += (metrics.sales || 0);
                bucket.signups += (metrics.signups || 0);
                bucket.calls += (metrics.calls || 0);
                bucket.revenue += (metrics.revenue || 0);
                funnelStepsList.forEach(stepName => {
                    bucket[stepName] = (bucket[stepName] || 0) + (metrics[stepName] || 0);
                });
                if (classified.id === 'youtube' && metrics.views != null) {
                    bucket.views += (metrics.views || 0);
                }
            });
        });

        return Object.entries(sourceStats)
            .map(([id, metrics]) => ({
                id,
                title: metrics.name,
                metrics,
                isVideo: id === 'youtube',
            }))
            .sort((a, b) => b.metrics.clicks - a.metrics.clicks)
            .slice(0, 6);
    }, [dailyData, customStartDate, customEndDate, funnelStepsList]);

    // Reset offer leaderboard tab if the selected tab has no data
    useEffect(() => {
        if (offerLeaderboardTab === 'emails' && !offerMetrics.some(o => o.metrics.signups > 0)) {
            setOfferLeaderboardTab('revenue');
        }
        if (offerLeaderboardTab === 'calls' && !offerMetrics.some(o => o.metrics.calls > 0)) {
            setOfferLeaderboardTab('revenue');
        }
    }, [offerMetrics, offerLeaderboardTab]);

    // Reset source leaderboard tab if the selected tab has no data
    useEffect(() => {
        if (sourceLeaderboardTab === 'emails' && !sourceMetrics.some(s => s.metrics.signups > 0)) {
            setSourceLeaderboardTab('revenue');
        }
        if (sourceLeaderboardTab === 'calls' && !sourceMetrics.some(s => s.metrics.calls > 0)) {
            setSourceLeaderboardTab('revenue');
        }
    }, [sourceMetrics, sourceLeaderboardTab]);

    const formatDate = (date) => {
        const d = new Date(date);
        if (viewBy === 'month') {
            return `${d.getMonth() + 1}/${d.getFullYear()}`;
        } else if (viewBy === 'week') {
            return `${d.getMonth() + 1}/${d.getDate()}`;
        }
        return `${d.getMonth() + 1}/${d.getDate()}`;
    };

    // Helper function to format numbers
    const formatNumber = (num) => {
        if (num >= 1000000) {
            return (num / 1000000).toFixed(1) + 'M';
        }
        if (num >= 1000) {
            return (num / 1000).toFixed(1) + 'K';
        }
        return num.toString();
    };

    // Format currency function - simplified to just use USD
    const formatCurrency = (value) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: currency,
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(value);
    };

    // Helper function to calculate and format difference
    const formatDifference = (current, previous) => {
        if (previous === 0) return { text: 'No previous data', className: '' };

        const percentChange = ((current - previous) / previous) * 100;
        const formattedPercent = Math.abs(percentChange).toFixed(1);

        if (percentChange === 0) return { text: `Same as prev`, className: '' };
        const isPositive = percentChange > 0;
        return {
            text: `${isPositive ? '+' : '-'}${formattedPercent}% vs prev`,
            className: isPositive ? 'diff-positive' : 'diff-negative'
        };
    };

    const getMetricIndicator = (current, previous) => {
        if (previous === 0) return null;
        
        const percentChange = ((current - previous) / previous) * 100;
        
        if (percentChange >= 10) {
            return '↑'; // Up arrow for >= 10% increase
        } else if (percentChange <= -10) {
            return '↓'; // Down arrow for <= -10% decrease
        } else {
            return '✓'; // Checkmark for within ±10%
        }
    };

    const renderComparison = (current, previous) => {
        if (previous === 0) return <div className="metric-comparison">No previous data</div>;
        const percentChange = ((current - previous) / previous) * 100;
        if (percentChange === 0) return <div className="metric-comparison">Same as prev</div>;
        const isPositive = percentChange > 0;
        const arrow = isPositive ? '↑' : '↓';
        const formatted = Math.abs(percentChange).toFixed(1);
        return (
            <div className={`metric-comparison ${isPositive ? 'positive' : 'negative'}`}>
                {arrow} {formatted}% vs prev
            </div>
        );
    };

    const CustomTooltip = ({ active, payload, label, coordinate, viewBox }) => {
        if (!active || !payload?.length || activeMetric === 'summary') return null;
        const point = payload[0].payload;
        const originalValue = point[activeMetric];
        const displayName = activeMetric === 'attributed_clicks'
            ? 'Attributed clicks'
            : activeMetric === 'clicks'
                ? 'Traffic'
                : activeMetric === 'engagedViews'
                    ? 'Engaged views'
                    : payload[0].name || activeMetric;
        const contributors = point.contributors?.[activeMetric] || [];
        const isRevenue = activeMetric === 'revenue';
        const chartLeft = viewBox?.x ?? 0;
        const chartWidth = viewBox?.width ?? 0;
        const flipLeft = (coordinate?.x ?? 0) > chartLeft + chartWidth * 0.55;
        return (
            <div className={`custom-tooltip${flipLeft ? ' custom-tooltip--flip-left' : ''}`}>
                <p className="custom-tooltip-date">{formatDate(label)}</p>
                <p className="custom-tooltip-total">
                    {`${displayName}: ${isRevenue ? formatCurrency(originalValue || 0) : formatNumber(originalValue || 0)}`}
                </p>
                {contributors.length > 0 && (
                    <div className="custom-tooltip-list">
                        {contributors.map((item) => (
                            <div
                                key={item.kind === 'source' ? item.id : `${item.type}:${item.title}`}
                                className="custom-tooltip-row"
                            >
                                <span className="custom-tooltip-logo">
                                    {item.kind === 'source'
                                        ? <TrafficSourceLogo sourceId={item.id} name={item.name} />
                                        : <ResourceSourceLogo resourceType={item.type} size="sm" />}
                                </span>
                                <span className="custom-tooltip-title">
                                    {item.kind === 'source' ? item.name : item.title}
                                </span>
                                <span className="custom-tooltip-count">
                                    {isRevenue ? formatCurrency(item.revenue || 0) : formatNumber(item.count || 0)}
                                </span>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        );
    };

    // Handle offer selection
    const handleOfferClick = (offerName) => {
        // Toggle selection: if clicking the same offer, deselect it; otherwise select the new offer
        const newSelectedOffer = selectedOffer === offerName ? null : offerName;
        setSelectedOffer(newSelectedOffer);
        
        if (dailyData.length > 0 && customStartDate && customEndDate) {
            const periodDays = Math.ceil((customEndDate - customStartDate) / (1000 * 60 * 60 * 24));

            const sortedData = [...dailyData].sort((a, b) => new Date(a.date) - new Date(b.date));

            // Filter data to only include dates within the selected range and comparison range
            const startDateStr = localDateString(customStartDate);
            const endDateStr = localDateString(customEndDate);

            const previousStartDate = new Date(customStartDate);
            previousStartDate.setDate(previousStartDate.getDate() - periodDays);
            const previousStartDateStr = localDateString(previousStartDate);
            const previousEndDate = new Date(customStartDate);
            previousEndDate.setDate(previousEndDate.getDate() - 1);
            const previousEndDateStr = localDateString(previousEndDate);

            const currentPeriodData = sortedData.filter(day => day.date >= startDateStr && day.date <= endDateStr);
            const previousPeriodData = sortedData.filter(day => day.date >= previousStartDateStr && day.date <= previousEndDateStr);
            
            // Calculate totals for each period, filtering by offer if one is selected
            const currentTotals = currentPeriodData.reduce((acc, day) => {
                if (newSelectedOffer) {
                    // When an offer is selected, use that offer's clicks and sales
                    // but keep the overall views
                    const offerMetrics = day.metrics.byOffer[newSelectedOffer] || { clicks: 0, sales: 0, signups: 0, calls: 0, revenue: 0, ...funnelStepsList.reduce((acc, stepName) => {
                        acc[stepName] = 0;
                        return acc;
                    }, {}) };

                    const result = {
                        views: acc.views + (Number(day.metrics.overall.views) || 0),
                    engagedViews: acc.engagedViews + (Number(day.metrics.overall.engagedViews) || 0), // Always use overall views
                        clicks: acc.clicks + (Number(offerMetrics.clicks) || 0),
                        attributed_clicks: acc.attributed_clicks + (Number(offerMetrics.attributed_clicks) || 0),
                        sales: acc.sales + (Number(offerMetrics.sales) || 0),
                        calls: acc.calls + (Number(offerMetrics.calls) || 0),
                        signups: acc.signups + (Number(offerMetrics.signups) || 0),
                        revenue: acc.revenue + (Number(offerMetrics.revenue) || 0),
                    };
                    
                    // Add funnel step totals
                    funnelStepsList.forEach(stepName => {
                        result[stepName] = (acc[stepName] || 0) + (Number(offerMetrics[stepName]) || 0);
                    });
                
                    return result;
                    
                } else {
                    // If no offer is selected, use overall metrics
                    const result = {
                        views: acc.views + (Number(day.metrics.overall.views) || 0),
                    engagedViews: acc.engagedViews + (Number(day.metrics.overall.engagedViews) || 0),
                        clicks: acc.clicks + (Number(day.metrics.overall.clicks) || 0),
                    attributed_clicks: acc.attributed_clicks + (Number(day.metrics.overall.attributed_clicks) || 0),
                        sales: acc.sales + (Number(day.metrics.overall.sales) || 0),
                        signups: acc.signups + (Number(day.metrics.overall.signups) || 0),
                        revenue: acc.revenue + (Number(day.metrics.overall.revenue) || 0),
                        calls: acc.calls + (Number(day.metrics.overall.calls) || 0),
                    };
                
                    // Add funnel step totals
                    funnelStepsList.forEach(stepName => {
                        result[stepName] = (acc[stepName] || 0) + (Number(day.metrics.overall[stepName]) || 0);
                    });
                    
                    return result;
                }
            }, { views: 0, engagedViews: 0, clicks: 0, attributed_clicks: 0, sales: 0, signups: 0, calls: 0, revenue: 0, ...funnelStepsList.reduce((acc, stepName) => { acc[stepName] = 0; return acc; }, {}) });
            
            const previousTotals = previousPeriodData.reduce((acc, day) => {
                if (newSelectedOffer) {
                    // When an offer is selected, use that offer's clicks and sales
                    // but keep the overall views
                    const offerMetrics = day.metrics.byOffer[newSelectedOffer] || { clicks: 0, sales: 0, signups: 0, calls: 0, revenue: 0, ...funnelStepsList.reduce((acc, stepName) => {
                        acc[stepName] = 0;
                        return acc;
                    }, {}) };

                    const result = {
                        views: acc.views + (Number(day.metrics.overall.views) || 0),
                    engagedViews: acc.engagedViews + (Number(day.metrics.overall.engagedViews) || 0), // Always use overall views
                        clicks: acc.clicks + (Number(offerMetrics.clicks) || 0),
                        attributed_clicks: acc.attributed_clicks + (Number(offerMetrics.attributed_clicks) || 0),
                        sales: acc.sales + (Number(offerMetrics.sales) || 0),
                        calls: acc.calls + (Number(offerMetrics.calls) || 0),
                        signups: acc.signups + (Number(offerMetrics.signups) || 0),
                        revenue: acc.revenue + (Number(offerMetrics.revenue) || 0),
                    };
                    
                    // Add funnel step totals
                    funnelStepsList.forEach(stepName => {
                        result[stepName] = (acc[stepName] || 0) + (Number(offerMetrics[stepName]) || 0);
                    });
                
                    return result;
                    
                } else {
                    // If no offer is selected, use overall metrics
                    const result = {
                        views: acc.views + (Number(day.metrics.overall.views) || 0),
                    engagedViews: acc.engagedViews + (Number(day.metrics.overall.engagedViews) || 0),
                        clicks: acc.clicks + (Number(day.metrics.overall.clicks) || 0),
                    attributed_clicks: acc.attributed_clicks + (Number(day.metrics.overall.attributed_clicks) || 0),
                        sales: acc.sales + (Number(day.metrics.overall.sales) || 0),
                        signups: acc.signups + (Number(day.metrics.overall.signups) || 0),
                        revenue: acc.revenue + (Number(day.metrics.overall.revenue) || 0),
                        calls: acc.calls + (Number(day.metrics.overall.calls) || 0),
                    };
                
                    // Add funnel step totals
                    funnelStepsList.forEach(stepName => {
                        result[stepName] = (acc[stepName] || 0) + (Number(day.metrics.overall[stepName]) || 0);
                    });
                    
                    return result;
                }
            }, { views: 0, engagedViews: 0, clicks: 0, attributed_clicks: 0, sales: 0, signups: 0, calls: 0, revenue: 0, ...funnelStepsList.reduce((acc, stepName) => { acc[stepName] = 0; return acc; }, {}) });
            
            // Update period comparison state with funnel steps
            setPeriodComparison({
                views: { current: currentTotals.views, previous: previousTotals.views },
                engagedViews: { current: currentTotals.engagedViews, previous: previousTotals.engagedViews },
                clicks: { current: currentTotals.clicks, previous: previousTotals.clicks },
                attributed_clicks: { current: currentTotals.attributed_clicks || 0, previous: previousTotals.attributed_clicks || 0 },
                sales: { current: currentTotals.sales, previous: previousTotals.sales },
                signups: { current: currentTotals.signups, previous: previousTotals.signups },
                revenue: { current: currentTotals.revenue, previous: previousTotals.revenue },
                calls: { current: currentTotals.calls, previous: previousTotals.calls },
                ...funnelStepsList.reduce((acc, stepName) => {
                    acc[stepName] = {
                        current: currentTotals[stepName],
                        previous: previousTotals[stepName]
                    };
                    return acc;
                }, {})
            });
            setHasComparisonData(previousPeriodData.length > 0);
        }
    };

    // Handle source selection
    const handleSourceClick = (sourceName) => {
        // Toggle selection: if clicking the same source, deselect it; otherwise select the new source
        const newSelectedSource = selectedSource === sourceName ? null : sourceName;
        setSelectedSource(newSelectedSource);
        setSelectedOffer(null); // Clear offer selection when selecting a source
        
        // Recalculate period comparison data based on the selected source
        if (dailyData.length > 0) {
            const periodDays = Math.ceil((customEndDate - customStartDate) / (1000 * 60 * 60 * 24));

            const sortedData = [...dailyData].sort((a, b) => new Date(a.date) - new Date(b.date));

            // Filter data to only include dates within the selected range and comparison range
            const startDateStr = localDateString(customStartDate);
            const endDateStr = localDateString(customEndDate);

            const previousStartDate = new Date(customStartDate);
            previousStartDate.setDate(previousStartDate.getDate() - periodDays);
            const previousStartDateStr = localDateString(previousStartDate);
            const previousEndDate = new Date(customStartDate);
            previousEndDate.setDate(previousEndDate.getDate() - 1);
            const previousEndDateStr = localDateString(previousEndDate);

            const currentPeriodData = sortedData.filter(day => day.date >= startDateStr && day.date <= endDateStr);
            const previousPeriodData = sortedData.filter(day => day.date >= previousStartDateStr && day.date <= previousEndDateStr);
            
            // Calculate totals for each period, filtering by source if one is selected
            const currentTotals = currentPeriodData.reduce((acc, day) => {
                if (newSelectedSource) {
                    // When a source is selected, use that source's metrics
                    const sourceType = classifyResourceType(newSelectedSource).id;
                    const sourceMetrics = day.metrics.byType[sourceType] || { views: 0, engagedViews: 0, clicks: 0, sales: 0, signups: 0, revenue: 0 };
                    
                    const result = {
                        views: acc.views + (sourceType === 'youtube' ? (Number(sourceMetrics.views) || 0) : 0),
                        engagedViews: acc.engagedViews + (sourceType === 'youtube' ? (Number(sourceMetrics.engagedViews) || 0) : 0),
                        clicks: acc.clicks + (Number(sourceMetrics.clicks) || 0),
                        attributed_clicks: acc.attributed_clicks + (Number(sourceMetrics.attributed_clicks) || 0),
                        sales: acc.sales + (Number(sourceMetrics.sales) || 0),
                        signups: acc.signups + (Number(sourceMetrics.signups) || 0),
                        revenue: acc.revenue + (Number(sourceMetrics.revenue) || 0),
                        calls: acc.calls + (Number(sourceMetrics.calls) || 0)
                    };
                    
                    // Add funnel step totals
                    funnelStepsList.forEach(stepName => {
                        result[stepName] = (acc[stepName] || 0) + (Number(sourceMetrics[stepName]) || 0);
                    });
                    
                    return result;
                } else {
                    // If no source is selected, use overall metrics
                    const result = {
                        views: acc.views + (Number(day.metrics.overall.views) || 0),
                    engagedViews: acc.engagedViews + (Number(day.metrics.overall.engagedViews) || 0),
                        clicks: acc.clicks + (Number(day.metrics.overall.clicks) || 0),
                    attributed_clicks: acc.attributed_clicks + (Number(day.metrics.overall.attributed_clicks) || 0),
                        sales: acc.sales + (Number(day.metrics.overall.sales) || 0),
                        signups: acc.signups + (Number(day.metrics.overall.signups) || 0),
                        revenue: acc.revenue + (Number(day.metrics.overall.revenue) || 0),
                        calls: acc.calls + (Number(day.metrics.overall.calls) || 0)
                    };
                    
                    // Add funnel step totals
                    funnelStepsList.forEach(stepName => {
                        result[stepName] = (acc[stepName] || 0) + (Number(day.metrics.overall[stepName]) || 0);
                    });
                    
                    return result;
                }
            }, { views: 0, engagedViews: 0, clicks: 0, attributed_clicks: 0, sales: 0, signups: 0, revenue: 0, calls: 0, ...funnelStepsList.reduce((acc, stepName) => { acc[stepName] = 0; return acc; }, {}) });
            
            const previousTotals = previousPeriodData.reduce((acc, day) => {
                if (newSelectedSource) {
                    // When a source is selected, use that source's metrics
                    const sourceType = classifyResourceType(newSelectedSource).id;
                    const sourceMetrics = day.metrics.byType[sourceType] || { views: 0, engagedViews: 0, clicks: 0, sales: 0, signups: 0, calls: 0, revenue: 0, ...funnelStepsList.reduce((acc, stepName) => {
                        acc[stepName] = 0;
                        return acc;
                    }, {}) };

                    
                    const result = {
                        views: acc.views + (sourceType === 'youtube' ? (Number(sourceMetrics.views) || 0) : 0),
                        engagedViews: acc.engagedViews + (sourceType === 'youtube' ? (Number(sourceMetrics.engagedViews) || 0) : 0),
                        clicks: acc.clicks + (Number(sourceMetrics.clicks) || 0),
                        attributed_clicks: acc.attributed_clicks + (Number(sourceMetrics.attributed_clicks) || 0),
                        sales: acc.sales + (Number(sourceMetrics.sales) || 0),
                        signups: acc.signups + (Number(sourceMetrics.signups) || 0),
                        revenue: acc.revenue + (Number(sourceMetrics.revenue) || 0),
                        calls: acc.calls + (Number(sourceMetrics.calls) || 0)
                    };
                    
                    // Add funnel step totals
                    funnelStepsList.forEach(stepName => {
                        result[stepName] = (acc[stepName] || 0) + (Number(sourceMetrics[stepName]) || 0);
                    });
                    
                    return result;
                } else {
                    // If no source is selected, use overall metrics
                    const result = {
                        views: acc.views + (Number(day.metrics.overall.views) || 0),
                    engagedViews: acc.engagedViews + (Number(day.metrics.overall.engagedViews) || 0),
                        clicks: acc.clicks + (Number(day.metrics.overall.clicks) || 0),
                    attributed_clicks: acc.attributed_clicks + (Number(day.metrics.overall.attributed_clicks) || 0),
                        sales: acc.sales + (Number(day.metrics.overall.sales) || 0),
                        signups: acc.signups + (Number(day.metrics.overall.signups) || 0),
                        revenue: acc.revenue + (Number(day.metrics.overall.revenue) || 0),
                        calls: acc.calls + (Number(day.metrics.overall.calls) || 0)
                    };
                    
                    // Add funnel step totals
                    funnelStepsList.forEach(stepName => {
                        result[stepName] = (acc[stepName] || 0) + (Number(day.metrics.overall[stepName]) || 0);
                    });
                    
                    return result;
                }
            }, { views: 0, engagedViews: 0, clicks: 0, attributed_clicks: 0, sales: 0, signups: 0, revenue: 0, calls: 0, ...funnelStepsList.reduce((acc, stepName) => { acc[stepName] = 0; return acc; }, {}) });
            
            // Update period comparison state
            setPeriodComparison({
                views: { current: currentTotals.views, previous: previousTotals.views },
                engagedViews: { current: currentTotals.engagedViews, previous: previousTotals.engagedViews },
                clicks: { current: currentTotals.clicks, previous: previousTotals.clicks },
                attributed_clicks: { current: currentTotals.attributed_clicks || 0, previous: previousTotals.attributed_clicks || 0 },
                sales: { current: currentTotals.sales, previous: previousTotals.sales },
                signups: { current: currentTotals.signups, previous: previousTotals.signups },
                revenue: { current: currentTotals.revenue, previous: previousTotals.revenue },
                calls: { current: currentTotals.calls, previous: previousTotals.calls },
                ...funnelStepsList.reduce((acc, stepName) => {
                    acc[stepName] = {
                        current: currentTotals[stepName],
                        previous: previousTotals[stepName]
                    };
                    return acc;
                }, {})
            });
            
            setHasComparisonData(previousPeriodData.length > 0);
        }
    };

    // Add useEffect to fetch offers
    useEffect(() => {
        const fetchOffers = async () => {
            if (!username) return;
            
            const token = await getToken();
            try {
                const response = await axios.get(`https://${SERVER_URL}/api/offers/${username}`, {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                });
                // Extract the offers array from the response
                const fetchedOffers = response.data.offers || [];
                setOffers(fetchedOffers); // Store offers in state
                
                // Check if any offers have type 'signup'
                const hasSignups = fetchedOffers.some(offer => offer.offer_type === 'signup');
                const hasCalls = fetchedOffers.some(offer => offer.call_booking_required === true);
                setHasSignupOffers(hasSignups);
                setHasCallOffers(hasCalls);
            } catch (error) {
                setHasSignupOffers(false); // Set to false on error
            }
        };
        
        fetchOffers();
    }, [username, getToken]);

    const toggleStepVisibility = (stepName) => {
        setVisibleSteps(prev => ({
            ...prev,
            [stepName]: !prev[stepName]
        }));
    };

    const resetStepVisibility = () => {
        const defaultSteps = {};
        funnelStepsList.forEach(step => {
            defaultSteps[step] = true;
        });
        setVisibleSteps(defaultSteps);
    };

    const handleTimeframeChange = (option) => {
        setTimeframeOption(option);

        if (option !== 'custom') {
            const { startDate, endDate } = applyDashboardTimeframe(option);
            setCustomStartDate(startDate);
            setCustomEndDate(endDate);
        }
    };

    return (
        <div className="home-container">
            {isLoading ? (
                <div className="loading-overlay">
                    <div className="loading-text">Loading analytics data...</div>
                </div>
            ) : error ? (
                <div className="error-container">
                    <p>Error: {error}</p>
                    <button onClick={() => window.location.reload()}>Retry</button>
                </div>
            ) : loggingOutYt ? (
                <div className="loading-overlay">
                    <div className="loading-text">Logging out...</div>
                </div>
            ) : (
                <>
                    <div className="youtube-account-container">
                        {youtubeName ? (
                            <div className="youtube-account-info">
                                <p>
                                    Logged in as:
                                </p>
                                <div className="youtube-account-details">
                                    {youtubeThumbUrl && (
                                        <img 
                                            src={youtubeThumbUrl} 
                                            alt="Channel" 
                                            className="channel-thumbnail" 
                                            style={{ width: '28px', height: '28px', borderRadius: '50%', marginRight: '8px', verticalAlign: 'middle' }}
                                        />
                                    )}
                                    {youtubeName}
                                    <Icon 
                                        name="logout" 
                                        className="yt-logout-icon" 
                                        onClick={handleLogout}
                                    />
                                </div>
                            </div>
                        ) : (
                            <div className="youtube-account-info">
                                <button className="youtube-login-button" onClick={() => handleLoginWithYouTube(username)}>
                                    <Icon name="youtube" size={20} />
                                    Login with YouTube
                                </button>
                            </div>
                        )}
                    </div>
                    <div className="controls-container">

                        <div className="controls-analytics">
                            <div className="control-group-analytics">
                                <label>Date Range:</label>
                                <div className="timeframe-container">
                                    <select
                                        value={timeframeOption}
                                        onChange={(e) => handleTimeframeChange(e.target.value)}
                                        className="timeframe-selector"
                                    >
                                        <option value="30days">Last 30 Days</option>
                                        <option value="monthtodate">Month to Date</option>
                                        <option value="lastmonth">Previous Month</option>
                                        <option value="90days">Last 90 Days</option>
                                        <option value="6months">Last 6 Months</option>
                                        <option value="1year">Last 1 Year</option>
                                        <option value="yeartodate">Year to Date</option>
                                        <option value="custom">Custom</option>
                                    </select>
                                    
                                    {timeframeOption === 'custom' && (
                                        <div className="date-range-analytics">
                                            <DatePicker
                                                selected={customStartDate}
                                                onChange={(date) => setCustomStartDate(date)}
                                                selectsStart
                                                startDate={customStartDate}
                                                endDate={customEndDate}
                                                placeholderText="Start Date"
                                                dateFormat="MMM d, yyyy"
                                                maxDate={new Date()}
                                            />
                                            <span className="date-separator">to</span>
                                            <DatePicker
                                                selected={customEndDate}
                                                onChange={(date) => setCustomEndDate(date)}
                                                selectsEnd
                                                startDate={customStartDate}
                                                endDate={customEndDate}
                                                minDate={customStartDate}
                                                maxDate={new Date()}
                                                placeholderText="End Date"
                                                dateFormat="MMM d, yyyy"
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>

                            {offerMetrics.length > 0 && (
                                <div className="control-group-analytics">
                                    <label>Offer:</label>
                                    <select
                                        className="timeframe-selector offer-filter-select"
                                        value={selectedOffer || ''}
                                        onChange={(e) => {
                                            const newOffer = e.target.value || null;
                                            if (newOffer === null) {
                                                if (selectedOffer !== null) handleOfferClick(selectedOffer);
                                            } else if (newOffer !== selectedOffer) {
                                                // Different offer selected
                                                handleOfferClick(newOffer);
                                            }
                                        }}
                                    >
                                        <option value="">All Offers</option>
                                        {offerMetrics.map(o => (
                                            <option key={o.title} value={o.title}>{o.title}</option>
                                        ))}
                                    </select>
                                </div>
                            )}
                            <div className="control-group-analytics">
                            <div className="view-by-selector">
                                <label>View by:</label>
                                <select value={viewBy} onChange={(e) => setViewBy(e.target.value)}>
                                    <option value="day">Day</option>
                                    <option value="week">Week</option>
                                    <option value="month">Month</option>
                                </select>
                            </div>
                            </div>

                            {haveViews && (
                                <div className="control-group-analytics">
                                    <label className="engaged-views-toggle">
                                        <span>Engaged views</span>
                                        <span className="engaged-views-switch">
                                            <input
                                                type="checkbox"
                                                className="engaged-views-switch-input"
                                                checked={showEngagedViews}
                                                onChange={() => setShowEngagedViews(prev => !prev)}
                                            />
                                            <span className="engaged-views-switch-track">
                                                <span className="engaged-views-switch-thumb" />
                                            </span>
                                        </span>
                                    </label>
                                </div>
                            )}

                            {funnelStepsList.length > 0 && (
                                <div className="control-group-analytics">
                                    <label>Show steps:</label>
                                    <button 
                                        className="step-selector-toggle" 
                                        onClick={() => setIsStepSelectorOpen(!isStepSelectorOpen)}
                                        style={{marginLeft: '10px'}}
                                    >
                                        <Icon name={isStepSelectorOpen ? "columnsFill" : "columns"} size={16} /> 
                                        {Object.values(visibleSteps).filter(Boolean).length} selected
                                    </button>
                                </div>
                            )}
                            
                            {isStepSelectorOpen && (
                                <div className="step-selector-dropdown">
                                    <div className="step-selector-header">
                                        <h4>Show/Hide Steps</h4>
                                        <button className="reset-steps-button" onClick={resetStepVisibility}>
                                            Reset
                                        </button>
                                    </div>
                                    <div className="step-options">
                                        {funnelStepsList.map(stepName => (
                                            <label key={stepName}>
                                                <input 
                                                    type="checkbox" 
                                                    checked={visibleSteps[stepName] !== false} 
                                                    onChange={() => toggleStepVisibility(stepName)}
                                                />
                                                {stepName}
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                    {/* Only render metrics and charts if we have data */}
                    {dailyData.length > 0 ? (
                        <>
                            <div className={`metric-boxes ${hasSignupOffers && hasCallOffers ? 'with-signups-and-calls' : hasSignupOffers ? 'with-signups' : ''}`}>
                                {/* Always show the overall metrics when no offer is selected, 
                                    or show the selected offer's metrics when one is selected */}
                                <div 
                                    className={`metric-box ${activeMetric === 'summary' ? 'active' : ''}`}
                                    onClick={() => setActiveMetric('summary')}
                                >
                                    <div className="metric-box-header">
                                        <LayoutDashboard size={12} />
                                        <span className="metric-label">Summary</span>
                                    </div>
                                    <div className="metric-value">All</div>
                                    <div className="metric-comparison">Compare trends</div>
                                </div>
                                {/* Revenue */}
                                {hasMetricValue('revenue') && (
                                <div
                                    className={`metric-box ${activeMetric === 'revenue' ? 'active' : ''}`}
                                    onClick={() => setActiveMetric('revenue')}
                                >
                                    <div className="metric-box-header">
                                        <DollarSign size={12} />
                                        <span className="metric-label">Revenue</span>
                                    </div>
                                    <div className="metric-value">
                                        {formatCurrency(periodComparison.revenue?.current || 0)}
                                    </div>
                                    {periodComparison.revenue
                                        ? renderComparison(periodComparison.revenue.current, periodComparison.revenue.previous)
                                        : <div className="metric-comparison">No comparison data</div>}
                                </div>
                                )}
                                {/* Sales */}
                                {hasMetricValue('sales') && (
                                <div
                                    className={`metric-box ${activeMetric === 'sales' ? 'active' : ''}`}
                                    onClick={() => setActiveMetric('sales')}
                                >
                                    <div className="metric-box-header">
                                        <ShoppingBag size={12} />
                                        <span className="metric-label">Sales</span>
                                    </div>
                                    <div className="metric-value">
                                        {formatNumber(periodComparison.sales.current)}
                                    </div>
                                    {renderComparison(periodComparison.sales.current, periodComparison.sales.previous)}
                                </div>
                                )}
                                {/* Email Signups */}
                                {hasSignupOffers && hasMetricValue('signups') && (
                                    <div
                                        className={`metric-box ${activeMetric === 'signups' ? 'active' : ''}`}
                                        onClick={() => setActiveMetric('signups')}
                                    >
                                        <div className="metric-box-header">
                                            <Mail size={12} />
                                            <span className="metric-label">Email Signups</span>
                                        </div>
                                        <div className="metric-value">
                                            {formatNumber(periodComparison.signups.current)}
                                        </div>
                                        {renderComparison(periodComparison.signups.current, periodComparison.signups.previous)}
                                    </div>
                                )}
                                {/* Calls Booked */}
                                {hasCallOffers && hasMetricValue('calls') && (
                                    <div
                                        className={`metric-box ${activeMetric === 'calls' ? 'active' : ''}`}
                                        onClick={() => setActiveMetric('calls')}
                                    >
                                        <div className="metric-box-header">
                                            <PhoneCall size={12} />
                                            <span className="metric-label">Calls Booked</span>
                                        </div>
                                        <div className="metric-value">
                                            {formatNumber(periodComparison.calls?.current || 0)}
                                        </div>
                                        {renderComparison(periodComparison.calls?.current || 0, periodComparison.calls?.previous || 0)}
                                    </div>
                                )}
                                {/* Funnel step metric boxes */}
                                {funnelStepsList.filter(stepName => visibleSteps[stepName] !== false && hasMetricValue(stepName)).map(stepName => (
                                    <div
                                        key={stepName}
                                        className={`metric-box ${activeMetric === stepName ? 'active' : ''}`}
                                        onClick={() => setActiveMetric(stepName)}
                                    >
                                        <div className="metric-box-header">
                                            <Filter size={12} />
                                            <span className="metric-label">{stepName}</span>
                                        </div>
                                        <div className="metric-value">
                                            {formatNumber(periodComparison[stepName]?.current || 0)}
                                        </div>
                                        {periodComparison[stepName]
                                            ? renderComparison(periodComparison[stepName].current, periodComparison[stepName].previous)
                                            : <div className="metric-comparison">No comparison data</div>}
                                    </div>
                                ))}
                                {/* Attributed clicks */}
                                {hasMetricValue('attributed_clicks') && (
                                <div
                                    className={`metric-box ${activeMetric === 'attributed_clicks' ? 'active' : ''}`}
                                    onClick={() => setActiveMetric('attributed_clicks')}
                                >
                                    <div className="metric-box-header">
                                        <Link2 size={12} />
                                        <span className="metric-label">
                                            Attributed clicks
                                            <span
                                                className="metric-help"
                                                tabIndex={0}
                                                role="img"
                                                aria-label="Same as Traffic, excluding visits that were not tied to a tracked resource."
                                                onClick={(e) => e.stopPropagation()}
                                            >
                                                ?
                                                <span className="metric-help-tooltip">
                                                    Same as Traffic, excluding visits that were not tied to a tracked resource.
                                                </span>
                                            </span>
                                        </span>
                                    </div>
                                    <div className="metric-value">
                                        {formatNumber(periodComparison.attributed_clicks?.current || 0)}
                                    </div>
                                    {renderComparison(periodComparison.attributed_clicks?.current || 0, periodComparison.attributed_clicks?.previous || 0)}
                                </div>
                                )}
                                {/* Clicks */}
                                {hasMetricValue('clicks') && (
                                <div
                                    className={`metric-box ${activeMetric === 'clicks' ? 'active' : ''}`}
                                    onClick={() => setActiveMetric('clicks')}
                                >
                                    <div className="metric-box-header">
                                        <MousePointerClick size={12} />
                                        <span className="metric-label">
                                            Traffic
                                            <span
                                                className="metric-help"
                                                tabIndex={0}
                                                role="img"
                                                aria-label="This measures all visits to your landing page, not just tracked clicks"
                                                onClick={(e) => e.stopPropagation()}
                                            >
                                                ?
                                                <span className="metric-help-tooltip">
                                                    This measures all visits to your landing page, not just tracked clicks
                                                </span>
                                            </span>
                                        </span>
                                    </div>
                                    <div className="metric-value">
                                        {formatNumber(periodComparison.clicks.current)}
                                    </div>
                                    {renderComparison(periodComparison.clicks.current, periodComparison.clicks.previous)}
                                </div>
                                )}
                                {showEngagedViews && haveViews && hasMetricValue('engagedViews') && (
                                    <div
                                        className={`metric-box ${activeMetric === 'engagedViews' ? 'active' : ''}`}
                                        onClick={() => setActiveMetric('engagedViews')}
                                    >
                                        <div className="metric-box-header">
                                            <Eye size={12} />
                                            <span className="metric-label">Engaged Views</span>
                                        </div>
                                        <div className="metric-value">
                                            {formatNumber(periodComparison.engagedViews?.current || 0)}
                                        </div>
                                        {hasComparisonData
                                            ? renderComparison(periodComparison.engagedViews?.current || 0, periodComparison.engagedViews?.previous || 0)
                                            : <div className="metric-comparison">Loading...</div>}
                                    </div>
                                )}
                                {/* Views */}
                                {haveViews && hasMetricValue('views') && (
                                    <div
                                        className={`metric-box ${activeMetric === 'views' ? 'active' : ''}`}
                                        onClick={() => setActiveMetric('views')}
                                    >
                                        <div className="metric-box-header">
                                            <Eye size={12} />
                                            <span className="metric-label">Views</span>
                                        </div>
                                        <div className="metric-value">
                                            {formatNumber(periodComparison.views.current)}
                                        </div>
                                        {hasComparisonData
                                            ? renderComparison(periodComparison.views.current, periodComparison.views.previous)
                                            : <div className="metric-comparison">Loading...</div>}
                                    </div>
                                )}
                            </div>

                                <div className="chart">
                                    <div className="chart-header">
                                        {haveViews && (activeMetric === 'views' || activeMetric === 'engagedViews' || activeMetric === 'summary') && (
                                            <div className="views-notice">
                                                <small>*last 2 days' views data processing</small>
                                            </div>
                                        )}
                                    </div>
                                    <div className="graph-container">
                                        <ResponsiveContainer width="100%" height={400}>
                                            {activeMetric === 'summary' ? (
                                                <AreaChart data={getNormalizedData}>
                                                    <CartesianGrid strokeDasharray="0" horizontal={true} vertical={false}  />
                                                    <XAxis dataKey="date" tickFormatter={formatDate} />
                                                    <YAxis tickFormatter={(value) => `${value}%`} domain={[0, 100]} />
                                                    <Tooltip content={() => null} cursor={false} active={false} />
                                                    <Legend />
                                                    {haveViews && <Area 
                                                        type="linear"
                                                        dataKey="normalizedViews"
                                                        name="Views"
                                                        stroke="#00D3F2"
                                                        strokeWidth={2}
                                                        fill="#00D3F2"
                                                        fillOpacity={0.3}
                                                    />}
                                                    <Area 
                                                        type="linear"
                                                        dataKey="normalizedClicks"
                                                        name="Traffic"
                                                        stroke="#00F2A5"
                                                        strokeWidth={2}
                                                        fill="#00F2A5"
                                                        fillOpacity={0.3}
                                                    />
                                                    {hasCallOffers && <Area 
                                                        type="linear"
                                                        dataKey="normalizedCalls"
                                                        name="Calls"
                                                        stroke="#FF6B6B"
                                                        strokeWidth={2}
                                                        fill="#FF6B6B"
                                                        fillOpacity={0.3}
                                                    />}
                                                    <Area 
                                                        type="linear"
                                                        dataKey="normalizedSales"
                                                        name="Sales"
                                                        stroke="#A149D5"
                                                        strokeWidth={2}
                                                        fill="#A149D5"
                                                        fillOpacity={0.3}
                                                    />
                                                    {hasSignupOffers && <Area 
                                                        type="linear"
                                                        dataKey="normalizedSignups"
                                                        name="Emails"
                                                        stroke="#FFD700"
                                                        strokeWidth={2}
                                                        fill="#FFD700"
                                                        fillOpacity={0.3}
                                                    />}
                                                    <Area 
                                                        type="linear"
                                                        dataKey="normalizedRevenue"
                                                        name="Revenue"
                                                        stroke="#C5833E"
                                                        strokeWidth={2}
                                                        fill="#C5833E"
                                                        fillOpacity={0.3}
                                                    />
                                                </AreaChart>
                                            ) : (
                                                <AreaChart data={groupedProcessedData}>
                                                    <defs>
                                                        <pattern id="graphTexture" patternUnits="userSpaceOnUse" width="100%" height="100%">
                                                            <image href="/graph-texture.jpeg" width="100%" height="100%" preserveAspectRatio="xMidYMid none"/>
                                                        </pattern>
                                                    </defs>
                                                    <CartesianGrid strokeDasharray="0" horizontal={true} vertical={false} />
                                                    <XAxis dataKey="date" tickFormatter={formatDate} />
                                                    <YAxis />
                                                    <Tooltip
                                                        content={<CustomTooltip />}
                                                        cursor={{ stroke: 'var(--Stroke-Light)' }}
                                                        allowEscapeViewBox={{ x: true, y: true }}
                                                        wrapperStyle={{ background: 'transparent', border: 'none', boxShadow: 'none', outline: 'none', padding: 0, zIndex: 20 }}
                                                    />
                                                    <Area 
                                                        type="linear"
                                                        dataKey={activeMetric}
                                                        name={activeMetric === 'attributed_clicks' ? 'Attributed clicks' : activeMetric === 'clicks' ? 'Traffic' : activeMetric === 'engagedViews' ? 'Engaged views' : activeMetric}
                                                        stroke="#00D3F2"
                                                        strokeWidth={2}
                                                        fill="#00D3F2"
                                                        fillOpacity={0.3}
                                                        style={{
                                                            fill: 'url(/graph-texture.jpeg) lightgray 0% 0% / 30px 30px repeat'
                                                        }}
                                                    />
                                                </AreaChart>
                                            )}
                                        </ResponsiveContainer>
                                    </div>
                                </div>

                        </>
                    ) : (
                        <div className="no-data-container">
                            <p>No data available for the selected time period.</p>
                        </div>
                    )}
                                                                              {/* AI Insights Section */}
                        {/* <div className="ai-insights-section">
                                {(isProAccount || isFounder) ? (
                                    <>
                                        {!aiInsights && !aiInsightsLoading && (
                                            <button className="ai-insights-trigger" onClick={fetchAiInsights}>
                                                <Sparkles size={16} />
                                                Get AI Insights
                                            </button>
                                        )}
                                        {aiInsightsLoading && (
                                            <div className="ai-insights-loading">
                                                <div className="ai-insights-spinner" />
                                                <span>Analyzing your data...</span>
                                            </div>
                                        )}
                                        {aiInsights && aiInsights.insights && (
                                            <div className="ai-insights-panel">
                                                <div className="ai-insights-header">
                                                    <h3>AI Insights</h3>
                                                    <button className="ai-insights-close" onClick={() => setAiInsights(null)}>×</button>
                                                </div>
                                                <div className="ai-insights-grid">
                                                    <div className="ai-insights-col do-more">
                                                        <h4>Do more of</h4>
                                                        {aiInsights.insights.patternsToDoMore.map((item, i) => (
                                                            <div key={i} className="ai-insight-item">
                                                                <div className="ai-insight-bullet" />
                                                                <div>
                                                                    <div className="ai-insight-pattern">{item.pattern}</div>
                                                                    <div className="ai-insight-desc">{item.description}</div>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                    <div className="ai-insights-col do-less">
                                                        <h4>Do less of</h4>
                                                        {aiInsights.insights.patternsToDoLess.map((item, i) => (
                                                            <div key={i} className="ai-insight-item">
                                                                <div className="ai-insight-bullet" />
                                                                <div>
                                                                    <div className="ai-insight-pattern">{item.pattern}</div>
                                                                    <div className="ai-insight-desc">{item.description}</div>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </>
                                ) : (
                                    <div className="ai-insights-pro-gate">
                                        <Sparkles size={15} />
                                        <span>Get AI Insights</span>
                                        <span className="ai-insights-pro-badge">PRO</span>
                                    </div>
                                )}
                            </div>              Add offer leaderboard */}
                    <div className="offer-leaderboard-section">
                        <div className="offer-leaderboard-header">
                            <h2>OFFERS LEADERBOARD</h2>
                            <div className="offer-leaderboard-tabs">
                                <button 
                                    className={offerLeaderboardTab === 'revenue' ? 'active' : ''}
                                    onClick={() => setOfferLeaderboardTab('revenue')}
                                >
                                    Sales
                                </button>
                                {offerMetrics.some(o => o.metrics.signups > 0) && (
                                    <button 
                                        className={offerLeaderboardTab === 'emails' ? 'active' : ''}
                                        onClick={() => setOfferLeaderboardTab('emails')}
                                    >
                                        Email signups
                                    </button>
                                )}
                                {offerMetrics.some(o => o.metrics.calls > 0) && (
                                    <button 
                                        className={offerLeaderboardTab === 'calls' ? 'active' : ''}
                                        onClick={() => setOfferLeaderboardTab('calls')}
                                    >
                                        Calls
                                    </button>
                                )}
                                <button 
                                    className={offerLeaderboardTab === 'clicks' ? 'active' : ''}
                                    onClick={() => setOfferLeaderboardTab('clicks')}
                                >
                                    Traffic
                                </button>
                            </div>
                            <a href="/code" className="manage-offers-link">
                                Manage offers →
                            </a>
                        </div>

                        <div className="offer-leaderboard-table">
                        <div className="offer-table-header">
                                <span className="col-rank">#</span>
                                <span className="col-offer">OFFER</span>
                                <SortTh label="TRAFFIC"  sortKey="clicks"  sort={offerSort} onSort={handleOfferSort} className="col-clicks" />
                                {offerLeaderboardTab === 'revenue' && (
                                    <>
                                        <SortTh label="SALES"   sortKey="sales"   sort={offerSort} onSort={handleOfferSort} className="col-sales" />
                                        <SortTh label="CONV %"  sortKey="conv"    sort={offerSort} onSort={handleOfferSort} className="col-conv" />
                                        <SortTh label="REVENUE" sortKey="revenue" sort={offerSort} onSort={handleOfferSort} className="col-revenue" />
                                    </>
                                )}
                                {offerLeaderboardTab === 'emails' && (
                                    <>
                                        <SortTh label="EMAILS"  sortKey="emails"  sort={offerSort} onSort={handleOfferSort} className="col-emails" />
                                        <SortTh label="CONV %"  sortKey="conv"    sort={offerSort} onSort={handleOfferSort} className="col-conv" />
                                    </>
                                )}
                                {offerLeaderboardTab === 'calls' && (
                                    <>
                                        <SortTh label="CALLS"   sortKey="calls"   sort={offerSort} onSort={handleOfferSort} className="col-calls" />
                                        <SortTh label="CONV %"  sortKey="conv"    sort={offerSort} onSort={handleOfferSort} className="col-conv" />
                                    </>
                                )}
                            </div>

                            <div className="offer-table-body">
                                {(() => {
                                    const offerGetVal = (key, o) => {
                                        if (key === 'clicks')  return o.metrics.clicks;
                                        if (key === 'sales')   return o.metrics.sales;
                                        if (key === 'revenue') return o.metrics.revenue;
                                        if (key === 'emails')  return o.metrics.signups;
                                        if (key === 'calls')   return o.metrics.calls;
                                        if (key === 'conv') {
                                            const num = offerLeaderboardTab === 'emails' ? o.metrics.signups
                                                : offerLeaderboardTab === 'calls' ? o.metrics.calls
                                                : o.metrics.sales;
                                            return o.metrics.clicks > 0 ? num / o.metrics.clicks : 0;
                                        }
                                        return 0;
                                    };
                                    const filtered = offerMetrics.filter(offer => {
                                        if (offerLeaderboardTab === 'revenue') return offer.metrics.sales > 0;
                                        if (offerLeaderboardTab === 'emails') return offer.metrics.signups > 0;
                                        if (offerLeaderboardTab === 'calls') return offer.metrics.calls > 0;
                                        return offer.metrics.clicks > 0;
                                    });
                                    const defaultSort = (a, b) => {
                                        if (offerLeaderboardTab === 'revenue') return b.metrics.revenue - a.metrics.revenue;
                                        if (offerLeaderboardTab === 'emails') return b.metrics.signups - a.metrics.signups;
                                        if (offerLeaderboardTab === 'calls') return b.metrics.calls - a.metrics.calls;
                                        return b.metrics.clicks - a.metrics.clicks;
                                    };
                                    const sorted = offerSort.key
                                        ? [...filtered].sort((a, b) => {
                                            const dir = offerSort.dir === 'desc' ? -1 : 1;
                                            return (offerGetVal(offerSort.key, b) - offerGetVal(offerSort.key, a)) * dir;
                                          })
                                        : [...filtered].sort(defaultSort);
                                    return sorted.map((offer, index) => (
                                        <div key={index} className="offer-table-row">
                                            <span className="col-rank">{index + 1}</span>
                                            <span className="col-offer">{offer.title}</span>
                                            <span className="col-clicks">{formatNumber(offer.metrics.clicks)}</span>
                                            {offerLeaderboardTab === 'revenue' && (
                                                <>
                                                    <span className="col-sales">{formatNumber(offer.metrics.sales)}</span>
                                                    <span className={`col-conv ${offer.metrics.clicks > 0 && ((offer.metrics.sales / offer.metrics.clicks) * 100) > 3 ? 'high-conv' : ''}`}>
                                                        {offer.metrics.clicks > 0 
                                                            ? `${((offer.metrics.sales / offer.metrics.clicks) * 100).toFixed(1)}%`
                                                            : '0.0%'
                                                        }
                                                    </span>
                                                    <span className="col-revenue">{formatCurrency(offer.metrics.revenue)}</span>
                                                </>
                                            )}
                                            {offerLeaderboardTab === 'emails' && (
                                                <>
                                                    <span className="col-emails">{formatNumber(offer.metrics.signups)}</span>
                                                    <span className={`col-conv ${offer.metrics.clicks > 0 && ((offer.metrics.signups / offer.metrics.clicks) * 100) > 3 ? 'high-conv' : ''}`}>
                                                        {offer.metrics.clicks > 0 
                                                            ? `${((offer.metrics.signups / offer.metrics.clicks) * 100).toFixed(1)}%`
                                                            : '0.0%'
                                                        }
                                                    </span>
                                                </>
                                            )}
                                            {offerLeaderboardTab === 'calls' && (
                                                <>
                                                    <span className="col-calls">{formatNumber(offer.metrics.calls)}</span>
                                                    <span className={`col-conv ${offer.metrics.clicks > 0 && ((offer.metrics.calls / offer.metrics.clicks) * 100) > 3 ? 'high-conv' : ''}`}>
                                                        {offer.metrics.clicks > 0 
                                                            ? `${((offer.metrics.calls / offer.metrics.clicks) * 100).toFixed(1)}%`
                                                            : '0.0%'
                                                        }
                                                    </span>
                                                </>
                                            )}
                                        </div>
                                    ));
                                })()}
                            </div>
                        </div>
                    </div>

                    {/* Source Leaderboard Section */}
                    {sourceMetrics && sourceMetrics.length > 0 && (
                        <div className="offer-leaderboard-section">
                            <div className="offer-leaderboard-header">
                                <h2 className="offer-section-heading">PLATFORM LEADERBOARD</h2>
                                <div className="offer-leaderboard-tabs">
                                    <button
                                        className={sourceLeaderboardTab === 'revenue' ? 'active' : ''}
                                        onClick={() => setSourceLeaderboardTab('revenue')}
                                    >
                                        Sales
                                    </button>
                                    {sourceMetrics.some(s => s.metrics.signups > 0) && (
                                        <button
                                            className={sourceLeaderboardTab === 'emails' ? 'active' : ''}
                                            onClick={() => setSourceLeaderboardTab('emails')}
                                        >
                                            Email signups
                                        </button>
                                    )}
                                    {sourceMetrics.some(s => s.metrics.calls > 0) && (
                                        <button
                                            className={sourceLeaderboardTab === 'calls' ? 'active' : ''}
                                            onClick={() => setSourceLeaderboardTab('calls')}
                                        >
                                            Calls
                                        </button>
                                    )}
                                    <button
                                        className={sourceLeaderboardTab === 'clicks' ? 'active' : ''}
                                        onClick={() => setSourceLeaderboardTab('clicks')}
                                    >
                                        Traffic
                                    </button>
                                </div>
                                <a href="/content" className="manage-offers-link">
                                    See all data →
                                </a>
                            </div>

                            <div className="source-bar-list">
                                {(() => {
                                    const srcTabValue = (s) => {
                                        if (sourceLeaderboardTab === 'revenue') return s.metrics.revenue;
                                        if (sourceLeaderboardTab === 'emails')  return s.metrics.signups;
                                        if (sourceLeaderboardTab === 'calls')   return s.metrics.calls;
                                        return s.metrics.clicks;
                                    };
                                    const filtered = sourceMetrics
                                        .filter(source => {
                                            if (sourceLeaderboardTab === 'revenue') return source.metrics.sales > 0;
                                            if (sourceLeaderboardTab === 'emails')  return source.metrics.signups > 0;
                                            if (sourceLeaderboardTab === 'calls')   return source.metrics.calls > 0;
                                            return source.metrics.clicks > 0;
                                        })
                                        .sort((a, b) => srcTabValue(b) - srcTabValue(a));
                                    const total = filtered.reduce((sum, s) => sum + srcTabValue(s), 0);
                                    return filtered.map((source, index) => {
                                        const value = srcTabValue(source);
                                        const pct = total > 0 ? (value / total) * 100 : 0;
                                        const displayValue = sourceLeaderboardTab === 'revenue'
                                            ? formatCurrency(source.metrics.revenue)
                                            : formatNumber(value);
                                        return (
                                            <div key={index} className="source-bar-row">
                                                <div className="source-bar-info">
                                                    <div className="source-bar-label">
                                                        <span className="source-bar-icon">
                                                            <TrafficSourceLogo sourceId={source.id} name={source.title} />
                                                        </span>
                                                        <span className="source-bar-name">{source.title}</span>
                                                    </div>
                                                    <span className="source-bar-value">{displayValue} · {Math.round(pct)}%</span>
                                                </div>
                                                <div className="source-bar-track">
                                                    <div className="source-bar-fill" style={{ width: `${pct}%` }} />
                                                </div>
                                            </div>
                                        );
                                    });
                                })()}
                            </div>
                        </div>
                    )}

                    {/* Top Performing Content Section */}
                    {topResources && (
                        <div className="top-performing-section">
                            <div className="top-performing-header">
                                <h2>TOP PERFORMING CONTENT</h2>
                                <div className="top-performing-tabs">

                                        <button 
                                            className={topResourcesTab === 'sales' ? 'active' : ''}
                                            onClick={() => setTopResourcesTab('sales')}
                                        >
                                            Sales
                                        </button>

                                    {topResources.topCalls?.length > 0 && (
                                        <button 
                                            className={topResourcesTab === 'calls' ? 'active' : ''}
                                            onClick={() => setTopResourcesTab('calls')}
                                        >
                                            Calls
                                        </button>
                                    )}
                                    {topResources.topEmails?.length > 0 && (
                                        <button 
                                            className={topResourcesTab === 'emails' ? 'active' : ''}
                                            onClick={() => setTopResourcesTab('emails')}
                                        >
                                            Email signups
                                        </button>
                                    )}
                                    {topResources.topClicks?.length > 0 && (
                                        <button 
                                            className={topResourcesTab === 'clicks' ? 'active' : ''}
                                            onClick={() => setTopResourcesTab('clicks')}
                                        >
                                            Traffic
                                        </button>
                                    )}
                                </div>
                                <a href="/content" className="manage-offers-link">
                                    See all data →
                                </a>
                            </div>

                            <div className="top-performing-table">
                                <div className="table-header">
                                    <span className="col-rank">#</span>
                                    <span className="col-offer">CONTENT</span>
                                    <SortTh label="VIEWS"  sortKey="views"  sort={contentSort} onSort={handleContentSort} className="col-views" />
                                    <SortTh label="TRAFFIC" sortKey="clicks" sort={contentSort} onSort={handleContentSort} className="col-clicks" />
                                    {topResourcesTab !== 'clicks' && (
                                        <SortTh
                                            label={topResourcesTab === 'sales' ? 'SALES' : topResourcesTab === 'calls' ? 'CALLS' : 'EMAILS'}
                                            sortKey="metric"
                                            sort={contentSort}
                                            onSort={handleContentSort}
                                            className="col-metric"
                                        />
                                    )}
                                    <SortTh label="CONV %" sortKey="conv"    sort={contentSort} onSort={handleContentSort} className="col-conv" />
                                    {topResourcesTab === 'sales' && (
                                        <SortTh label="REVENUE" sortKey="revenue" sort={contentSort} onSort={handleContentSort} className="col-revenue" />
                                    )}
                                </div>

                                <div className="table-body">
                                    {(() => {
                                        const baseList =
                                            topResourcesTab === 'sales'  ? topResources.topRevenue :
                                            topResourcesTab === 'calls'  ? topResources.topCalls :
                                            topResourcesTab === 'emails' ? topResources.topEmails :
                                            topResources.topClicks;
                                        const cntGetVal = (key, r) => {
                                            if (key === 'views')   return r.views || 0;
                                            if (key === 'clicks')  return r.clicks || 0;
                                            if (key === 'conv')    return parseFloat(r.conversion_rate) || 0;
                                            if (key === 'revenue') return r.revenue || 0;
                                            if (key === 'metric')  return topResourcesTab === 'sales' ? (r.sales || 0)
                                                                        : topResourcesTab === 'calls' ? (r.call_bookings || 0)
                                                                        : (r.email_signups || 0);
                                            return 0;
                                        };
                                        const defaultSort = (a, b) => {
                                            if (topResourcesTab === 'sales')  return b.revenue - a.revenue;
                                            if (topResourcesTab === 'calls')  return b.call_bookings - a.call_bookings;
                                            if (topResourcesTab === 'emails') return b.email_signups - a.email_signups;
                                            return b.clicks - a.clicks;
                                        };
                                        const sorted = contentSort.key
                                            ? [...baseList].sort((a, b) => {
                                                const dir = contentSort.dir === 'desc' ? -1 : 1;
                                                return (cntGetVal(contentSort.key, b) - cntGetVal(contentSort.key, a)) * dir;
                                              })
                                            : [...baseList].sort(defaultSort);
                                        return sorted.map((resource, index) => (
                                            <div key={resource.resource_id} className="table-row">
                                                <span className="col-rank">{String(index + 1).padStart(2, '0')}</span>
                                                <div className="col-offer">
                                                    <div className="offer-title">
                                                        {resource.youtube_title || resource.name}
                                                    </div>
                                                </div>
                                                <span className="col-views">
                                                    {resource.views === 0 ? 'N/A' : formatNumber(resource.views)}
                                                </span>
                                                <span className="col-clicks">
                                                    {formatNumber(resource.clicks)}
                                                </span>
                                                {topResourcesTab !== 'clicks' && (
                                                    <span className="col-metric">
                                                        {topResourcesTab === 'sales'  ? formatNumber(resource.sales) :
                                                         topResourcesTab === 'calls'  ? formatNumber(resource.call_bookings) :
                                                         formatNumber(resource.email_signups)}
                                                    </span>
                                                )}
                                                <span className="col-conv">
                                                    {`${resource.conversion_rate}%`}
                                                </span>
                                                {topResourcesTab === 'sales' && (
                                                    <span className="col-revenue">
                                                        {formatCurrency(resource.revenue)}
                                                    </span>
                                                )}
                                            </div>
                                        ));
                                    })()}
                                </div>
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default Home;
